import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { formatDate, formatTime, formatDateTime } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Truck,
  Loader2,
  FileUp,
  FileCheck,
  X,
  Save,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import exifr from "exifr";

async function getPhotoTimestamp(file) {
  try {
    const exif = await exifr.parse(file, ["DateTimeOriginal"]);
    if (exif?.DateTimeOriginal) return { timestamp: exif.DateTimeOriginal.toISOString(), source: "exif" };
  } catch (err) {
    // EXIF metadata is best-effort — fall back to upload timestamp below.
    console.debug("exifr parse failed", err);
  }
  return { timestamp: new Date().toISOString(), source: "upload" };
}

const DEST_TYPES = ["Recycler", "Converter", "Incinerator", "Landfill", "Exporter"];

function nowLocalIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ShipmentForm() {
  const navigate = useNavigate();
  const { id: editShipmentId } = useParams();
  const isEdit = Boolean(editShipmentId);
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");
  const [outputs, setOutputs] = useState([]);
  const [selected, setSelected] = useState({});
  const [destinations, setDestinations] = useState([newDestRow()]);
  // "traced" (default) requires source batch selection; "surplus_sale" hides
  // step 1 (the batch picker) and skips the mass-balance check — every other
  // required field stays exactly the same. Only settable on brand-new
  // shipments, never on edit/resubmit.
  const [shipmentSource, setShipmentSource] = useState("traced");
  const isSurplus = shipmentSource === "surplus_sale";
  const [busy, setBusy] = useState(false);
  const [directory, setDirectory] = useState([]);  // active destinations directory
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [rejectionReason, setRejectionReason] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftList, setDraftList] = useState([]);       // drafts owned by this user
  const [activeDraft, setActiveDraft] = useState(null); // { id, label, updated_at }

  useEffect(() => {
    api.get("/destinations", { params: { status: "Active" } })
      .then((r) => setDirectory(r.data))
      .catch(() => {});
    // Drafts are irrelevant when we're editing a submitted shipment.
    if (!isEdit) {
      api.get("/shipments/drafts")
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : [];
          setDraftList(list);
          if (resumeId) {
            const match = list.find((x) => x.id === resumeId);
            if (match) resumeDraft(match);
          }
        })
        .catch((e) => console.debug("shipment drafts load failed", e));
    }
  }, [isEdit]);

  async function refreshDirectory() {
    try {
      const { data } = await api.get("/destinations", { params: { status: "Active" } });
      setDirectory(data);
      return data;
    } catch {
      return directory;
    }
  }

  function newDestRow() {
    return {
      _uid: `dest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      directory_id: "",
      destination_type: "Recycler",
      destination_name: "",
      materials: [{ material_type: "PET", weight_kg: "" }],
      transaction_at: nowLocalIso(),
      invoice_path: "",
      invoice_uploading: false,
      invoice_filename: "",
      invoice_id: "",
      material_photos: [],   // [{path, timestamp, source, uploading?}]
      payment_proofs: [],    // [{path, date, filename?, uploading?}]
      additional_info: "",   // free-text notes, optional
    };
  }

  useEffect(() => {
    const params = isEdit ? { include_shipment_id: editShipmentId } : undefined;
    api.get("/outputs/available", { params })
      .then((r) => setOutputs(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
  }, [isEdit, editShipmentId]);

  // Prefill state from the rejected shipment when in edit mode
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/shipments/${editShipmentId}`);
        if (cancelled) return;
        setRejectionReason(data.rejection_reason || "");
        const ids = data.source_output_ids || [];
        const sel = {};
        ids.forEach((oid) => { sel[oid] = true; });
        setSelected(sel);
        // Group destinations by (directory_id || name+type)+invoice_path+invoice_id+transaction_at so
        // multi-material rows that were expanded on submit collapse back into one card.
        const rawDests = data.destinations || [];
        const groupKey = (d) => [
          d.directory_id || `${d.destination_name}|${d.destination_type}`,
          d.invoice_path || "",
          d.invoice_id || "",
          d.transaction_at || "",
        ].join("::");
        const groups = new Map();
        rawDests.forEach((d) => {
          const k = groupKey(d);
          if (!groups.has(k)) groups.set(k, []);
          groups.get(k).push(d);
        });
        const dests = Array.from(groups.values()).map((items) => {
          const first = items[0];
          const txAt = first.transaction_at ? new Date(first.transaction_at) : new Date();
          const pad = (n) => String(n).padStart(2, "0");
          const local = `${txAt.getFullYear()}-${pad(txAt.getMonth() + 1)}-${pad(txAt.getDate())}T${pad(txAt.getHours())}:${pad(txAt.getMinutes())}`;
          return {
            directory_id: first.directory_id || "",
            destination_type: first.destination_type || "Recycler",
            destination_name: first.destination_name || "",
            materials: items.map((d) => ({
              material_type: d.material_type || "PET",
              weight_kg: String(d.weight_kg ?? ""),
            })),
            transaction_at: local,
            invoice_path: first.invoice_path || "",
            invoice_uploading: false,
            invoice_filename: first.invoice_path ? (first.invoice_path.split("/").pop() || "invoice") : "",
            invoice_id: first.invoice_id || "",
            material_photos: (first.material_photos || []).map((p) => ({
              path: p.path, timestamp: p.timestamp, source: p.source, filename: p.filename || (p.path ? p.path.split("/").pop() : ""), uploading: false,
            })),
            payment_proofs: (first.payment_proofs || []).map((s) => ({
              path: s.path, date: s.date || "", filename: s.filename || (s.path ? s.path.split("/").pop() : ""), uploading: false,
            })),
          };
        });
        if (dests.length > 0) setDestinations(dests);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editShipmentId]);

  // Aggregate available kg per material across selected outputs. We use
  // `remaining_kg` from the backend (weight_kg minus already-shipped) so an
  // output that was partially shipped shows its residual, not its original
  // size. Fallback to `weight_kg` if the field is missing (defensive — the
  // /outputs/available endpoint should always populate it post-migration).
  const availableByMaterial = useMemo(() => {
    const m = {};
    outputs.forEach((o) => {
      if (selected[o.id]) {
        const kg = o.remaining_kg != null ? parseFloat(o.remaining_kg) : parseFloat(o.weight_kg) || 0;
        m[o.material_type] = (m[o.material_type] || 0) + (kg || 0);
      }
    });
    return m;
  }, [outputs, selected]);

  const allocatedByMaterial = useMemo(() => {
    const m = {};
    destinations.forEach((d) => {
      (d.materials || []).forEach((sub) => {
        const w = parseFloat(sub.weight_kg) || 0;
        m[sub.material_type] = (m[sub.material_type] || 0) + w;
      });
    });
    return m;
  }, [destinations]);

  const totalAvailable = useMemo(
    () => Object.values(availableByMaterial).reduce((s, v) => s + v, 0),
    [availableByMaterial]
  );
  const totalAllocated = useMemo(
    () => Object.values(allocatedByMaterial).reduce((s, v) => s + v, 0),
    [allocatedByMaterial]
  );

  // In surplus-sale mode there's no upstream batch to compare against, so the
  // mass-balance overflow check must be inert on the UI as well as the API —
  // otherwise the submit button stays disabled and the "Over-allocated"
  // banner fires even for valid surplus shipments.
  const overflowMaterials = isSurplus
    ? []
    : Object.entries(allocatedByMaterial)
        .filter(([m, w]) => w > (availableByMaterial[m] || 0) + 0.001)
        .map(([m]) => m);

  function setDest(idx, key, val) {
    setDestinations((prev) => prev.map((d, i) => (i === idx ? { ...d, [key]: val } : d)));
  }
  function addDest() {
    setDestinations((prev) => [...prev, newDestRow()]);
  }
  function removeDest(idx) {
    setDestinations((prev) => prev.filter((_, i) => i !== idx));
  }

  async function uploadFile(idx, file, pathKey, filenameKey, uploadingKey, label) {
    if (!file) return;
    if (
      !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type)
    ) {
      toast.error("PDF, JPG, PNG or WebP only");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error(`${label} must be 12 MB or smaller`);
      return;
    }
    setDest(idx, uploadingKey, true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDestinations((prev) =>
        prev.map((d, i) =>
          i === idx
            ? { ...d, [pathKey]: data.storage_path, [filenameKey]: file.name, [uploadingKey]: false }
            : d
        )
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setDest(idx, uploadingKey, false);
    }
  }

  function uploadInvoice(idx, file) {
    return uploadFile(idx, file, "invoice_path", "invoice_filename", "invoice_uploading", "Invoice");
  }

  // ===== Per-destination MATERIAL PHOTOS (up to 3) =====
  async function addDestPhoto(idx, file) {
    if (!file) return;
    const dest = destinations[idx];
    if ((dest.material_photos || []).length >= 3) {
      toast.error("Max 3 photos per destination");
      return;
    }
    const tmpKey = `tmp_${Date.now()}_${Math.random()}`;
    const { timestamp, source } = await getPhotoTimestamp(file);
    setDestinations((prev) =>
      prev.map((d, i) =>
        i === idx
          ? { ...d, material_photos: [...(d.material_photos || []), { tmpKey, path: null, timestamp, source, uploading: true, filename: file.name }] }
          : d
      )
    );
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data?.path || res.data?.storage_path || "";
      setDestinations((prev) =>
        prev.map((d, i) =>
          i === idx
            ? {
                ...d,
                material_photos: (d.material_photos || []).map((p) =>
                  p.tmpKey === tmpKey ? { ...p, path, uploading: false } : p
                ),
              }
            : d
        )
      );
    } catch {
      setDestinations((prev) =>
        prev.map((d, i) =>
          i === idx
            ? { ...d, material_photos: (d.material_photos || []).filter((p) => p.tmpKey !== tmpKey) }
            : d
        )
      );
      toast.error("Photo upload failed");
    }
  }
  function removeDestPhoto(idx, photoIdx) {
    setDestinations((prev) =>
      prev.map((d, i) =>
        i === idx ? { ...d, material_photos: (d.material_photos || []).filter((_, k) => k !== photoIdx) } : d
      )
    );
  }

  // ===== Per-destination PAYMENT SLIPS (up to 5) =====
  function addDestSlip(idx) {
    setDestinations((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        if ((d.payment_proofs || []).length >= 5) return d;
        return { ...d, payment_proofs: [...(d.payment_proofs || []), { path: null, date: "", filename: "", uploading: false }] };
      })
    );
  }
  function removeDestSlip(idx, slipIdx) {
    setDestinations((prev) =>
      prev.map((d, i) =>
        i === idx ? { ...d, payment_proofs: (d.payment_proofs || []).filter((_, k) => k !== slipIdx) } : d
      )
    );
  }
  function updateDestSlipDate(idx, slipIdx, date) {
    setDestinations((prev) =>
      prev.map((d, i) =>
        i === idx
          ? { ...d, payment_proofs: (d.payment_proofs || []).map((s, k) => (k === slipIdx ? { ...s, date } : s)) }
          : d
      )
    );
  }
  async function handleDestSlipFile(idx, slipIdx, file) {
    if (!file) return;
    setDestinations((prev) =>
      prev.map((d, i) =>
        i === idx
          ? { ...d, payment_proofs: (d.payment_proofs || []).map((s, k) => (k === slipIdx ? { ...s, filename: file.name, uploading: true } : s)) }
          : d
      )
    );
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = res.data?.storage_path || res.data?.path || "";
      setDestinations((prev) =>
        prev.map((d, i) =>
          i === idx
            ? { ...d, payment_proofs: (d.payment_proofs || []).map((s, k) => (k === slipIdx ? { ...s, path, uploading: false } : s)) }
            : d
        )
      );
    } catch {
      setDestinations((prev) =>
        prev.map((d, i) =>
          i === idx
            ? { ...d, payment_proofs: (d.payment_proofs || []).map((s, k) => (k === slipIdx ? { ...s, uploading: false } : s)) }
            : d
        )
      );
      toast.error("Slip upload failed");
    }
  }

  function clearInvoice(idx) {
    setDestinations((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, invoice_path: "", invoice_filename: "" } : d))
    );
  }

  // ---------- Draft helpers ----------
  // Snapshot the entire form (selected outputs + destinations, un-collapsed)
  // as a plain-JSON payload. Validation is intentionally skipped — a draft
  // is allowed to contain partial or invalid state.
  function snapshotDraftPayload() {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    // Store `destinations` in the same "grouped per partner" shape as the
    // UI so we can restore lossless.
    return {
      source_output_ids: ids,
      destinations: destinations.map((d) => ({
        destination_type: d.destination_type,
        destination_name: d.destination_name || "",
        transaction_at: d.transaction_at || null,
        invoice_path: d.invoice_path || null,
        invoice_filename: d.invoice_filename || null,
        invoice_id: d.invoice_id || null,
        directory_id: d.directory_id || null,
        material_photos: d.material_photos || [],
        payment_proofs: d.payment_proofs || [],
        additional_info: d.additional_info || "",
        materials: (d.materials || []).map((m) => ({
          material_type: m.material_type,
          weight_kg: m.weight_kg === "" || m.weight_kg == null ? null : parseFloat(m.weight_kg),
        })),
      })),
    };
  }

  function resumeDraft(d) {
    const restored = {};
    for (const id of d.source_output_ids || []) restored[id] = true;
    setSelected(restored);
    if (Array.isArray(d.destinations) && d.destinations.length) {
      setDestinations(d.destinations.map((raw) => {
        const base = newDestRow();
        return {
          ...base,
          destination_type: raw.destination_type || base.destination_type,
          destination_name: raw.destination_name || "",
          transaction_at: raw.transaction_at || base.transaction_at,
          invoice_path: raw.invoice_path || "",
          invoice_filename: raw.invoice_filename || "",
          invoice_id: typeof raw.invoice_id === "string" ? raw.invoice_id : "",
          directory_id: raw.directory_id || null,
          material_photos: Array.isArray(raw.material_photos) ? raw.material_photos : [],
          payment_proofs: Array.isArray(raw.payment_proofs) ? raw.payment_proofs : [],
          additional_info: typeof raw.additional_info === "string" ? raw.additional_info : "",
          materials: (Array.isArray(raw.materials) && raw.materials.length
            ? raw.materials
            : [{ material_type: "PET", weight_kg: "" }]
          ).map((m) => ({
            material_type: m.material_type || "PET",
            weight_kg: m.weight_kg == null ? "" : String(m.weight_kg),
          })),
        };
      }));
    }
    setActiveDraft({ id: d.id, label: d.label, updated_at: d.updated_at });
    toast.success(`Resumed draft: ${d.label || "Untitled"}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startFresh() {
    setSelected({});
    setDestinations([newDestRow()]);
    setActiveDraft(null);
  }

  async function saveDraft() {
    setSavingDraft(true);
    try {
      const payload = snapshotDraftPayload();
      let saved;
      if (activeDraft?.id) {
        const { data } = await api.patch(`/shipments/drafts/${activeDraft.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/shipments/drafts", payload);
        saved = data;
      }
      setActiveDraft({ id: saved.id, label: saved.label, updated_at: saved.updated_at });
      setDraftList((prev) => [saved, ...prev.filter((x) => x.id !== saved.id)]);
      toast.success(activeDraft?.id ? "Draft updated" : "Draft saved — you can resume it later.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDraft(false);
    }
  }

  async function deleteDraftFromList(draftId) {
    if (!window.confirm("Discard this saved shipment draft?")) return;
    try {
      await api.delete(`/shipments/drafts/${draftId}`);
      setDraftList((prev) => prev.filter((d) => d.id !== draftId));
      if (activeDraft?.id === draftId) setActiveDraft(null);
      toast.success("Draft discarded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function submit() {
    if (busy) return;
    setBusy(true);
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!isSurplus && ids.length === 0) { setBusy(false); return toast.error("Select at least one output batch"); }
    if (destinations.length === 0) { setBusy(false); return toast.error("Add at least one destination"); }
    if (!isSurplus && overflowMaterials.length > 0) {
      setBusy(false);
      return toast.error(`Over-allocated: ${overflowMaterials.join(", ")}`);
    }
    for (const d of destinations) {
      if (!d.destination_name.trim()) { setBusy(false); return toast.error("Each partner needs a name"); }
      if (!d.transaction_at) { setBusy(false); return toast.error("Each partner needs a transaction date"); }
      const mats = d.materials || [];
      if (mats.length === 0) {
        setBusy(false);
        return toast.error("Each partner needs at least one material line");
      }
      for (const sub of mats) {
        if (!sub.weight_kg || parseFloat(sub.weight_kg) <= 0) {
          setBusy(false);
          return toast.error("Each material line needs a positive weight");
        }
        if (!isSurplus && !availableByMaterial[sub.material_type]) {
          setBusy(false);
          return toast.error(`No ${sub.material_type} in selected batches`);
        }
      }
      // Surplus sales must be backed by an invoice or a payment slip so they
      // stay auditable — mirrors the backend check.
      if (isSurplus) {
        const hasInvoice = Boolean(d.invoice_path);
        const hasPayment = (d.payment_proofs || []).some((s) => s.path);
        if (!hasInvoice && !hasPayment) {
          setBusy(false);
          return toast.error("Surplus sales require an invoice or payment proof on every partner");
        }
      }
    }
    try {
      const endpoint = isEdit ? `/shipments/${editShipmentId}/resubmit` : "/shipments";
      // Expand each partner card's materials[] into separate destination entries
      // — shared partner metadata (name, type, invoice, photos, slips, date) is duplicated.
      const expanded = destinations.flatMap((d) =>
        (d.materials || []).map((sub) => ({
          destination_type: d.destination_type,
          destination_name: d.destination_name.trim(),
          material_type: sub.material_type,
          weight_kg: parseFloat(sub.weight_kg),
          transaction_at: new Date(d.transaction_at).toISOString(),
          invoice_path: d.invoice_path || null,
          invoice_id: (d.invoice_id || "").trim() || null,
          material_photos: (d.material_photos || [])
            .filter((p) => p.path)
            .map((p) => ({ path: p.path, timestamp: p.timestamp, source: p.source })),
          payment_proofs: (d.payment_proofs || [])
            .filter((s) => s.path)
            .map((s) => ({ path: s.path, date: s.date || null })),
          directory_id: d.directory_id || null,
          additional_info: (d.additional_info || "").trim() || null,
        }))
      );
      await api.post(endpoint, {
        source_output_ids: isSurplus ? [] : ids,
        destinations: expanded,
        admin_status: "pending",
        submitted_draft_id: activeDraft?.id || null,
        shipment_source: isSurplus ? "surplus_sale" : "traced",
      });
      toast.success(isEdit ? "Shipment resubmitted for review" : "Shipment submitted");
      setActiveDraft(null);
      navigate("/facility");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Topbar subtitle={isEdit ? "Re-edit shipment" : "Shipment"} />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="shipment-form-page">
        <button
          onClick={() => navigate("/facility")}
          data-testid="back-to-facility-btn"
          className="flex items-center gap-1.5 text-sm text-[#3F3F3F] hover:text-[#056839]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to facility
        </button>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">
            {isEdit ? "Re-edit & resubmit" : "New shipment"}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-1">
            {isEdit ? "Fix and resubmit this shipment" : "Ship batches to their final destination"}
          </h1>
          <p className="text-sm text-[#3F3F3F] mt-1">
            {isEdit
              ? "Update any field below — destinations, weights, invoices or proofs — and resubmit to admin for another review."
              : "Pick processed batches, then split them across recyclers, converters, landfill or incineration."}
          </p>
        </div>

        {isEdit && rejectionReason && (
          <div className="rounded-xl border border-[#A0522D]/30 bg-[#A0522D]/10 p-4" data-testid="rejection-reason-banner">
            <div className="text-xs uppercase tracking-wider text-[#A0522D] font-semibold">Rejected by admin — reason</div>
            <div className="text-sm text-[#3F3F3F] mt-1">{rejectionReason}</div>
          </div>
        )}

        {!isEdit && (draftList.length > 0 || activeDraft) && (
          <Card
            className="border-[#E4D5B7] bg-[#FBF6EB] rounded-xl"
            data-testid="drafts-chooser"
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Save className="w-4 h-4 text-[#A0522D]" />
                  <div className="text-sm font-semibold text-[#000000]">
                    Saved drafts ({draftList.length})
                  </div>
                </div>
                {activeDraft && (
                  <button
                    type="button"
                    onClick={startFresh}
                    data-testid="start-fresh-btn"
                    className="text-xs inline-flex items-center gap-1 text-[#056839] hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Start a fresh entry
                  </button>
                )}
              </div>
              {draftList.length === 0 ? (
                <div className="text-xs text-[#3F3F3F]/70 italic">No saved drafts yet.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {draftList.map((d) => {
                    const isActive = activeDraft?.id === d.id;
                    return (
                      <div
                        key={d.id}
                        data-testid={`draft-card-${d.id}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                          isActive
                            ? "border-[#056839] bg-white shadow-sm"
                            : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#000000] truncate">
                            {d.label || "Untitled draft"}
                            {isActive && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-[#056839] font-semibold">
                                Editing
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#3F3F3F]/70 mt-0.5">
                            Saved {formatDateTime(d.updated_at)}
                            {Array.isArray(d.destinations) && d.destinations.length
                              ? ` · ${d.destinations.length} partner${d.destinations.length !== 1 ? "s" : ""}`
                              : ""}
                          </div>
                        </div>
                        {!isActive && (
                          <button
                            type="button"
                            onClick={() => resumeDraft(d)}
                            data-testid={`resume-draft-${d.id}`}
                            className="text-xs px-2.5 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                          >
                            Resume
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteDraftFromList(d.id)}
                          data-testid={`delete-draft-${d.id}`}
                          className="p-1 rounded-md text-[#A0522D] hover:bg-[#A0522D]/10"
                          title="Discard this draft"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {loadingEdit && (
          <div className="flex items-center gap-2 text-sm text-[#3F3F3F]/70" data-testid="loading-edit">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading shipment…
          </div>
        )}

        {/* Shipment source toggle (traced ⇄ surplus_sale). Only shown when
            creating a brand-new shipment — never on edit/resubmit, where the
            source was fixed at create time. The toggle ONLY hides Step 1
            (the source batch picker); every other field (destination, weight,
            material type, photo, payment proof) stays required. */}
        {!isEdit && (
          <div
            role="tablist"
            aria-label="Shipment source"
            className="inline-flex rounded-xl border border-[#D2E4D6] p-1 bg-white"
            data-testid="shipment-source-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!isSurplus}
              onClick={() => setShipmentSource("traced")}
              data-testid="shipment-source-traced"
              className={`px-4 h-9 text-sm font-medium rounded-lg transition-colors ${
                !isSurplus
                  ? "bg-[#056839] text-white"
                  : "text-[#3F3F3F] hover:bg-[#F0FAF4]"
              }`}
            >
              Traced batch
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isSurplus}
              onClick={() => setShipmentSource("surplus_sale")}
              data-testid="shipment-source-surplus"
              className={`px-4 h-9 text-sm font-medium rounded-lg transition-colors ${
                isSurplus
                  ? "bg-[#A0522D] text-white"
                  : "text-[#3F3F3F] hover:bg-[#FBF3EA]"
              }`}
            >
              Surplus sale
            </button>
          </div>
        )}
        {isSurplus && (
          <div
            className="text-xs text-[#A0522D] bg-[#FBF3EA] border border-[#E4D5B7] rounded-lg px-3 py-2"
            data-testid="surplus-help"
          >
            Surplus sale: no linked processing batch. An invoice or payment
            proof is required on every partner.
          </div>
        )}

        {/* Step 1: select output batches — hidden in surplus mode */}
        {!isSurplus && (
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">1 · Select output batches</h2>
              <div className="text-sm text-[#3F3F3F]">
                Selected:{" "}
                <span className="font-mono font-semibold text-[#056839]" data-testid="total-selected">
                  {totalAvailable.toFixed(2)} kg
                </span>
              </div>
            </div>
            {outputs.length === 0 ? (
              <div className="text-sm text-[#3F3F3F]/60 italic" data-testid="no-outputs">
                No unshipped output batches available. Run a processing first.
              </div>
            ) : (
              <>
                {(() => {
                  const allSelected = outputs.every((o) => selected[o.id]);
                  return (
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          if (allSelected) {
                            setSelected({});
                          } else {
                            const next = {};
                            for (const o of outputs) next[o.id] = true;
                            setSelected(next);
                          }
                        }}
                        data-testid="select-all-outputs"
                        className="text-[#056839] font-medium hover:underline"
                      >
                        {allSelected ? "Unselect all" : `Select all (${outputs.length})`}
                      </button>
                      <span className="text-[#3F3F3F]/60">
                        {Object.values(selected).filter(Boolean).length} of {outputs.length} selected
                      </span>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="outputs-list">
                {outputs.map((o) => (
                  <label
                    key={o.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selected[o.id]
                        ? "border-[#056839] bg-[#D2E4D6]/40"
                        : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
                    }`}
                  >
                    <Checkbox
                      checked={!!selected[o.id]}
                      onCheckedChange={(v) =>
                        setSelected((p) => ({ ...p, [o.id]: !!v }))
                      }
                      data-testid={`output-checkbox-${o.id}`}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#000000]">
                        {o.material_type} · {o.form}{" "}
                        <span className="font-mono text-[#056839]">
                          {(o.remaining_kg != null ? o.remaining_kg : o.weight_kg)} kg
                        </span>
                        {o.remaining_kg != null && o.weight_kg != null && Number(o.remaining_kg) < Number(o.weight_kg) - 0.001 && (
                          <span className="ml-2 text-[10px] font-mono text-[#A0522D] uppercase tracking-wider" data-testid={`output-partial-${o.id}`}>
                            of {o.weight_kg} kg
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#3F3F3F]/70 truncate">
                        {(o.processing_types || []).join(" + ")} ·{" "}
                        {o.processing_date
                          ? formatDate(o.processing_date)
                          : ""}
                      </div>
                    </div>
                  </label>
                ))}
                </div>
              </>
            )}

            {/* available materials summary */}
            {Object.keys(availableByMaterial).length > 0 && (
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#D2E4D6]" data-testid="available-materials">
                {Object.entries(availableByMaterial).map(([m, w]) => {
                  const used = allocatedByMaterial[m] || 0;
                  const remaining = w - used;
                  const over = remaining < -0.001;
                  return (
                    <div
                      key={m}
                      className={`text-xs rounded-md px-2.5 py-1.5 font-medium ${
                        over
                          ? "bg-[#A0522D]/15 text-[#A0522D]"
                          : remaining > 0.001
                          ? "bg-[#E4D5B7]/60 text-[#3F3F3F]"
                          : "bg-[#D2E4D6] text-[#056839]"
                      }`}
                    >
                      {m}:{" "}
                      <span className="font-mono">
                        {used.toFixed(2)} / {w.toFixed(2)} kg
                      </span>
                      {remaining > 0.001 && (
                        <span className="ml-1 opacity-70">({remaining.toFixed(2)} left)</span>
                      )}
                      {over && <span className="ml-1">over by {(-remaining).toFixed(2)}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Selected-outputs breakdown — big, prominent totals so the facility
             user knows exactly what quantity of each material they can allocate
             across destinations below. Mirrors the equivalent summary on the
             Processing form. */}
        {Object.keys(availableByMaterial).length > 0 && (
          <Card
            className="border-[#056839]/25 bg-[#D2E4D6]/30 rounded-xl"
            data-testid="selected-materials-summary"
          >
            <CardContent className="p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#056839] font-semibold">
                    In your selected batch
                  </div>
                  <div className="text-xs text-[#3F3F3F]/80 mt-0.5">
                    Use these totals to size each destination allocation below.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(availableByMaterial)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([mat, kg]) => (
                      <div
                        key={mat}
                        data-testid={`selected-material-${mat}`}
                        className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#056839]/30"
                      >
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-[#3F3F3F]">
                          {mat}
                        </span>
                        <span className="font-mono text-sm font-semibold text-[#056839]">
                          {kg.toFixed(1)} kg
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: destinations */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">2 · Destinations</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={addDest}
                data-testid="add-destination-btn"
                className="border-[#056839] text-[#056839] hover:bg-[#D2E4D6]"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add destination
              </Button>
            </div>
            <div className="space-y-4" data-testid="destinations-list">
              {destinations.map((d, idx) => (
                <DestinationRow
                  key={d._uid || `dest-${idx}`}
                  idx={idx}
                  data={d}
                  setDest={setDest}
                  onUploadInvoice={uploadInvoice}
                  onClearInvoice={clearInvoice}
                  onAddPhoto={addDestPhoto}
                  onRemovePhoto={removeDestPhoto}
                  onAddSlip={addDestSlip}
                  onRemoveSlip={removeDestSlip}
                  onSlipFile={handleDestSlipFile}
                  onSlipDate={updateDestSlipDate}
                  removable={destinations.length > 1}
                  onRemove={() => removeDest(idx)}
                  availableMaterials={Object.keys(availableByMaterial)}
                  directory={directory}
                  refreshDirectory={refreshDirectory}
                  isSurplus={isSurplus}
                  applyDirectoryToRow={(dirEntry) =>
                    setDestinations((prev) =>
                      prev.map((r, i) =>
                        i === idx
                          ? {
                              ...r,
                              directory_id: dirEntry.id,
                              destination_name: dirEntry.name,
                              destination_type: dirEntry.type,
                            }
                          : r
                      )
                    )
                  }
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-[#D2E4D6]">
              {!isSurplus && (
                <div className="text-sm text-[#3F3F3F]">
                  Total selected:{" "}
                  <span className="font-mono font-semibold">{totalAvailable.toFixed(2)} kg</span>
                </div>
              )}
              <div className="text-sm text-[#3F3F3F]">
                Total shipped:{" "}
                <span className="font-mono font-semibold">{totalAllocated.toFixed(2)} kg</span>
              </div>
              {!isSurplus && (
                <div className="text-sm text-[#3F3F3F]">
                  Residue:{" "}
                  <span
                    className={`font-mono font-semibold ${
                      overflowMaterials.length > 0 ? "text-[#A0522D]" : "text-[#056839]"
                    }`}
                    data-testid="shipment-residue"
                  >
                    {(totalAvailable - totalAllocated).toFixed(2)} kg
                  </span>
                </div>
              )}
            </div>
            {overflowMaterials.length > 0 && (
              <div className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg">
                Over-allocated: {overflowMaterials.join(", ")}. Reduce destination weights or pick more
                batches.
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/facility")}
            data-testid="cancel-shipment-btn"
          >
            Cancel
          </Button>
          {!isEdit && (
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={savingDraft || busy}
              data-testid="save-draft-btn"
              className="border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D]/10 h-11 px-5"
            >
              {savingDraft ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save draft</>
              )}
            </Button>
          )}
          <Button
            onClick={submit}
            disabled={busy || overflowMaterials.length > 0 || savingDraft}
            data-testid="submit-shipment-btn"
            className="gfs-bg-primary hover:bg-[#044E2A] text-white h-11 px-6"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
            {isEdit ? "Resubmit to admin" : "Submit shipment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DestinationRow({
  idx,
  data,
  setDest,
  onUploadInvoice,
  onClearInvoice,
  onAddPhoto,
  onRemovePhoto,
  onAddSlip,
  onRemoveSlip,
  onSlipFile,
  onSlipDate,
  removable,
  onRemove,
  availableMaterials,
  directory,
  refreshDirectory,
  applyDirectoryToRow,
  isSurplus,
}) {
  const fileRef = useRef(null);
  const matRef = useRef(null);
  const slipRefs = useRef({});
  const [showAddNew, setShowAddNew] = useState(false);
  const [newDest, setNewDest] = useState({
    name: "",
    type: "Recycler",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
  });
  const [savingNew, setSavingNew] = useState(false);
  function resetNewDest() {
    setNewDest({ name: "", type: "Recycler", contact_person: "", phone: "", email: "", address: "" });
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4 bg-[#F9F8F6] rounded-xl border border-[#E4D5B7]/50">
      <div className="md:col-span-7">
        <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
          Treatment partner
        </Label>
        <Select
          value={data.directory_id || ""}
          onValueChange={(v) => {
            if (v === "__add__") {
              setShowAddNew(true);
              return;
            }
            const entry = directory.find((d) => d.id === v);
            if (entry) applyDirectoryToRow(entry);
          }}
        >
          <SelectTrigger className="h-10 mt-1" data-testid={`dest-directory-${idx}`}>
            <SelectValue placeholder="Select from directory…" />
          </SelectTrigger>
          <SelectContent>
            {directory.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}  ({d.type})
              </SelectItem>
            ))}
            <SelectItem value="__add__">+ Add new treatment partner…</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-3">
        <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
          Destination type
        </Label>
        <Select
          value={data.destination_type}
          onValueChange={(v) => setDest(idx, "destination_type", v)}
        >
          <SelectTrigger className="h-10 mt-1" data-testid={`dest-type-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DEST_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="md:col-span-2">
        <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
          Date &amp; time
        </Label>
        <Input
          type="datetime-local"
          value={data.transaction_at}
          onChange={(e) => setDest(idx, "transaction_at", e.target.value)}
          data-testid={`dest-date-${idx}`}
          className="h-10 mt-1"
        />
      </div>

      {/* Materials sub-rows (each partner can ship multiple materials) */}
      <div className="md:col-span-12">
        <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
          Materials &amp; weights <span className="text-[#A0522D] normal-case">*</span>
        </Label>
        <div className="mt-1 space-y-2">
          {(data.materials || []).map((sub, mIdx) => (
            <div key={mIdx} className="flex gap-2 items-end" data-testid={`dest-mat-row-${idx}-${mIdx}`}>
              <div className="flex-1">
                <Select
                  value={sub.material_type}
                  onValueChange={(v) =>
                    setDest(idx, "materials",
                      (data.materials || []).map((s, k) => k === mIdx ? { ...s, material_type: v } : s)
                    )
                  }
                >
                  <SelectTrigger className="h-10" data-testid={`dest-material-${idx}-${mIdx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(availableMaterials.length > 0
                      ? availableMaterials
                      : ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"]
                    ).map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="kg"
                  value={sub.weight_kg}
                  onChange={(e) =>
                    setDest(idx, "materials",
                      (data.materials || []).map((s, k) => k === mIdx ? { ...s, weight_kg: e.target.value } : s)
                    )
                  }
                  data-testid={`dest-weight-${idx}-${mIdx}`}
                  className="h-10"
                />
              </div>
              {(data.materials || []).length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setDest(idx, "materials",
                      (data.materials || []).filter((_, k) => k !== mIdx)
                    )
                  }
                  data-testid={`dest-mat-remove-${idx}-${mIdx}`}
                  className="h-10 px-2 text-[#A0522D] hover:bg-[#A0522D]/10 rounded-md"
                  aria-label="Remove material"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setDest(idx, "materials",
                [...(data.materials || []), { material_type: "PET", weight_kg: "" }]
              )
            }
            data-testid={`dest-mat-add-${idx}`}
            className="text-xs text-[#056839] hover:underline mt-1"
          >
            + Add material
          </button>
        </div>
      </div>

      {/* Evidence uploads row: invoice + multi-photos + multi-payment-slips */}
      <div className="md:col-span-12 grid grid-cols-1 gap-3">
        {/* Invoice */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
            Sales invoice{" "}
            {isSurplus ? (
              <span className="normal-case tracking-normal text-[#A0522D]" data-testid="invoice-required-hint">
                required for surplus sales (invoice or payment slip)
              </span>
            ) : (
              <span className="normal-case tracking-normal text-[#3F3F3F]/50">optional</span>
            )}
          </Label>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid={`dest-invoice-input-${idx}`}
            onChange={(e) => onUploadInvoice(idx, e.target.files?.[0])}
          />
          {!data.invoice_path ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid={`dest-invoice-pick-${idx}`}
              disabled={data.invoice_uploading}
              className="mt-1 w-full inline-flex items-center justify-center gap-2 text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#E4D5B7] bg-white hover:border-[#69A488] text-[#3F3F3F] disabled:opacity-60"
            >
              {data.invoice_uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              {data.invoice_uploading ? "Uploading…" : "Invoice (PDF / image)"}
            </button>
          ) : (
            <div
              className="mt-1 w-full inline-flex items-center gap-2 text-sm px-3 h-10 rounded-lg bg-[#D2E4D6] text-[#056839] font-medium"
              data-testid={`dest-invoice-attached-${idx}`}
            >
              <FileCheck className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{data.invoice_filename || "Attached"}</span>
              <button
                type="button"
                onClick={() => onClearInvoice(idx)}
                data-testid={`dest-invoice-clear-${idx}`}
                className="text-[#A0522D] hover:text-[#804124]"
                aria-label="Remove invoice"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Invoice ID (optional, free text) */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
            Invoice ID{" "}
            <span className="normal-case tracking-normal text-[#3F3F3F]/50">optional</span>
          </Label>
          <Input
            type="text"
            value={data.invoice_id || ""}
            onChange={(e) => setDest(idx, "invoice_id", e.target.value)}
            placeholder="e.g. INV-2026-0142"
            maxLength={80}
            data-testid={`dest-invoice-id-${idx}`}
            className="mt-1 h-10"
          />
        </div>

        {/* Material photos (up to 3 with EXIF) */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
            Material photos <span className="normal-case tracking-normal text-[#3F3F3F]/50">up to 3</span>
          </Label>
          <input
            ref={matRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid={`dest-material-input-${idx}`}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) await onAddPhoto(idx, f);
            }}
          />
          <div className="mt-1 flex flex-wrap gap-2">
            {(data.material_photos || []).map((p, j) => (
              <div key={p.path || p.storage_path || `photo-${j}`} className="relative w-20 h-20 rounded-lg border border-[#D2E4D6] bg-white overflow-hidden flex items-center justify-center" data-testid={`dest-photo-${idx}-${j}`}>
                {p.uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#69A488]" />
                ) : (
                  <div className="text-center text-[9px] text-[#3F3F3F]/70 px-1">
                    <FileCheck className="w-4 h-4 text-[#056839] mx-auto" />
                    <div className="truncate w-full mt-1">{p.filename || "photo"}</div>
                    {p.timestamp && <div className="text-[#69A488] mt-0.5">{formatTime(p.timestamp)}{p.source === "exif" ? " 📷" : " ⬆"}</div>}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemovePhoto(idx, j)}
                  data-testid={`dest-photo-remove-${idx}-${j}`}
                  className="absolute -top-1.5 -right-1.5 bg-[#A0522D] text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] hover:bg-[#804124]"
                  aria-label="Remove photo"
                >
                  ×
                </button>
              </div>
            ))}
            {(data.material_photos || []).length < 3 && (
              <button
                type="button"
                onClick={() => matRef.current?.click()}
                data-testid={`dest-photo-add-${idx}`}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E4D5B7] bg-white hover:border-[#69A488] flex flex-col items-center justify-center text-[#3F3F3F]"
              >
                <FileUp className="w-4 h-4" />
                <span className="text-[10px] mt-1">Add photo</span>
              </button>
            )}
          </div>
        </div>

        {/* Payment slips (up to 5, each with date) */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
            Payment slips <span className="normal-case tracking-normal text-[#3F3F3F]/50">up to 5, with date</span>
          </Label>
          <div className="mt-1 space-y-2">
            {(data.payment_proofs || []).map((slip, j) => (
              <div key={slip.path || slip._uid || `slip-${j}`} className="flex gap-2 items-center p-2 border border-[#D2E4D6] rounded-lg bg-white" data-testid={`dest-slip-row-${idx}-${j}`}>
                <input
                  ref={(el) => { slipRefs.current[`${idx}-${j}`] = el; }}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid={`dest-slip-input-${idx}-${j}`}
                  onChange={(e) => onSlipFile(idx, j, e.target.files?.[0])}
                />
                {!slip.path ? (
                  <button
                    type="button"
                    onClick={() => slipRefs.current[`${idx}-${j}`]?.click()}
                    disabled={slip.uploading}
                    data-testid={`dest-slip-pick-${idx}-${j}`}
                    className="flex-1 inline-flex items-center gap-2 text-sm px-3 h-9 rounded-md border border-dashed border-[#E4D5B7] hover:border-[#69A488] disabled:opacity-60"
                  >
                    {slip.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                    <span className="truncate">{slip.uploading ? "Uploading…" : slip.filename || "Bank transfer / receipt"}</span>
                  </button>
                ) : (
                  <div className="flex-1 inline-flex items-center gap-2 text-sm px-3 h-9 rounded-md bg-[#D2E4D6] text-[#056839]">
                    <FileCheck className="w-4 h-4 shrink-0" />
                    <span className="truncate">{slip.filename || "Slip"}</span>
                  </div>
                )}
                <input
                  type="date"
                  value={slip.date || ""}
                  onChange={(e) => onSlipDate(idx, j, e.target.value)}
                  data-testid={`dest-slip-date-${idx}-${j}`}
                  className="h-9 rounded-md border border-[#D2E4D6] px-2 text-xs w-36"
                />
                <button
                  type="button"
                  onClick={() => onRemoveSlip(idx, j)}
                  data-testid={`dest-slip-remove-${idx}-${j}`}
                  className="text-[#A0522D] hover:text-[#804124] p-1"
                  aria-label="Remove slip"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(data.payment_proofs || []).length < 5 && (
              <button
                type="button"
                onClick={() => onAddSlip(idx)}
                data-testid={`dest-slip-add-${idx}`}
                className="text-sm text-[#056839] hover:underline"
              >
                + Add payment slip
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Additional information — free-text notes, per destination */}
      <div className="md:col-span-12">
        <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
          Additional information <span className="normal-case tracking-normal text-[#3F3F3F]/50">(optional)</span>
        </Label>
        <textarea
          value={data.additional_info || ""}
          onChange={(e) => setDest(idx, "additional_info", e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="Freight terms, contact person, quality remarks, dispute notes…"
          data-testid={`dest-additional-info-${idx}`}
          className="w-full mt-1 rounded-md border border-[#D2E4D6] bg-white px-3 py-2 text-sm text-[#3F3F3F] focus:outline-none focus:ring-2 focus:ring-[#056839]/30"
        />
      </div>

      <div className="md:col-span-12 flex items-end justify-end">
        {removable && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            data-testid={`dest-remove-${idx}`}
            className="text-[#A0522D] h-10 w-10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Quick-add destination dialog (same fields as Directory partners) */}
      <Dialog open={showAddNew} onOpenChange={(o) => !o && (setShowAddNew(false), resetNewDest())}>
        <DialogContent className="max-w-lg" data-testid={`quick-add-destination-${idx}`}>
          <DialogHeader>
            <DialogTitle>Add new treatment partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#3F3F3F]/70">
              Saved to your directory so you can pick it next time.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Name <span className="text-[#A0522D] normal-case">*</span>
                </Label>
                <Input
                  value={newDest.name}
                  onChange={(e) => setNewDest({ ...newDest, name: e.target.value })}
                  placeholder="Paprec PET Recycling"
                  data-testid={`quick-add-name-${idx}`}
                  className="mt-1 h-11"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                  Destination type <span className="text-[#A0522D] normal-case">*</span>
                </Label>
                <Select value={newDest.type} onValueChange={(v) => setNewDest({ ...newDest, type: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid={`quick-add-type-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEST_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Contact person</Label>
                <Input
                  value={newDest.contact_person}
                  onChange={(e) => setNewDest({ ...newDest, contact_person: e.target.value })}
                  data-testid={`quick-add-contact-${idx}`}
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Phone</Label>
                <Input
                  value={newDest.phone}
                  onChange={(e) => setNewDest({ ...newDest, phone: e.target.value })}
                  data-testid={`quick-add-phone-${idx}`}
                  className="mt-1 h-11"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Email</Label>
                <Input
                  type="email"
                  value={newDest.email}
                  onChange={(e) => setNewDest({ ...newDest, email: e.target.value })}
                  data-testid={`quick-add-email-${idx}`}
                  className="mt-1 h-11"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Address / location</Label>
                <textarea
                  value={newDest.address}
                  onChange={(e) => setNewDest({ ...newDest, address: e.target.value })}
                  rows={2}
                  data-testid={`quick-add-address-${idx}`}
                  className="mt-1 w-full rounded-lg border-2 border-[#E4D5B7] focus:border-[#056839] focus:outline-none focus:ring-2 focus:ring-[#056839]/15 px-3 py-2 text-sm bg-white"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddNew(false); resetNewDest(); }} data-testid={`quick-add-cancel-${idx}`}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!newDest.name.trim()) {
                  toast.error("Name is required");
                  return;
                }
                setSavingNew(true);
                try {
                  const { data: created } = await api.post("/destinations", {
                    name: newDest.name.trim(),
                    type: newDest.type,
                    contact_person: newDest.contact_person.trim() || null,
                    phone: newDest.phone.trim() || null,
                    email: newDest.email.trim() || null,
                    address: newDest.address.trim() || null,
                    status: "Active",
                  });
                  toast.success("Destination saved to directory");
                  const list = await refreshDirectory();
                  const entry = list.find((d) => d.id === created.id) || created;
                  applyDirectoryToRow(entry);
                  setShowAddNew(false);
                  resetNewDest();
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || e.message);
                } finally {
                  setSavingNew(false);
                }
              }}
              disabled={savingNew}
              data-testid={`quick-add-save-${idx}`}
              className="gfs-bg-primary hover:bg-[#044E2A] text-white"
            >
              {savingNew ? "Saving…" : "Save and use"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
